import React from 'react';
import { toPayloadNumber } from '../../utils/moneyPayload';
import useUnreadableNumberEntry from './useUnreadableNumberEntry';

/**
 * THE non-money number input — a multiplier, a figure, a count. The twin of
 * `ui/CurrencyInput` without the money: no £, no 2dp snap, no separator strip.
 * Added 2026-10-28 (input audit HIGH 1 + HIGH 2).
 *
 * ═════ 🚨 WHY IT EXISTS — MALFORMED IS NEVER BLANK ═════
 * A bare `<input type="number" onChange={(e) => set(e.target.value)}>` hands
 * `''` up for text the browser cannot read (`validity.badInput`: Firefox `1,5`,
 * `12abc`, `1.5.5`; every engine `-`, `1e`, `.`), while the box still SHOWS the
 * text. Where blank means "follow the level above" that silently changed pay
 * with a 200: a production's Camera O/T ×2.5 override went back to Pact, and a
 * crew grade's / member's multiplier back to the production's / grade's.
 * 🔑 This control emits `MALFORMED_NUMBER` instead — non-blank, non-numeric, so
 * `isMalformedAmount` (`utils/moneyPayload.js`) is TRUE for it and the caller's
 * own validator refuses the save by name. The mechanism (and its three
 * measurements) is `./useUnreadableNumberEntry.js`, SHARED with CurrencyInput —
 * never copied.
 * 🚨 **THE CALLER'S VALIDATOR IS THE GUARD.** `toPayloadNumber(marker, null)` is
 * `null`, which on a nullable column reads exactly like blank — so a caller
 * must refuse the marker BEFORE its payload builder runs.
 *
 * 🪤 **TYPE IS `text` AT REST, `number` WHILE FOCUSED**, exactly like
 * CurrencyInput, and for the same reason: the resting box must be able to SHOW
 * the marker (a number box would sanitise it to `""`, hiding the error).
 *
 * 🔑 **NO BLUR SNAP, EVER.** A time-and-a-third is `1.3333`; nothing here may
 * round. Blank stays blank, a real `0` stays `0` (`??`, never `||`).
 *
 * 🚨 **A DROP REPLACES THE WHOLE VALUE** (`CurrencyInput.js`'s THE DROP RULING —
 * insert-at-caret makes the number a function of a few pixels). The dropped
 * text is trimmed and handed over AS TYPED: unlike money there are no
 * separators to strip, so `1,5` arrives as `'1,5'`, which the caller's
 * validator refuses rather than this control guessing. A file drop (no text)
 * changes nothing but is still claimed, so the tab cannot navigate away.
 *
 * Options are NATIVE HINTS, never the gate: `integer` (step 1 + numeric keypad),
 * `min` / `max` / `step`, `allowNegative` (drops the default `min="0"`). The
 * gate is the caller's validator — `numberEntryProblem` below — and the server.
 * Any other prop (`aria-label`, `data-changed`, …) lands on the `<input>`.
 *
 * @param {string|number|null|undefined} value
 * @param {(next: string) => void} onChange  Receives the raw STRING, or the marker.
 */
export const MALFORMED_NUMBER = 'Invalid number';

/**
 * The validator's question, asked of what this control emits. → `null` when the
 * value is acceptable (BLANK INCLUDED — what blank means is the caller's rule),
 * else `'malformed' | 'negative' | 'fraction' | 'too_small' | 'too_large'`.
 * Same shape screen as every payload builder (`toPayloadNumber`), so a value
 * this calls fine is a value the builder sends as that number.
 */
const BLANK = Symbol('blank');
export function numberEntryProblem(value, { integer = false, allowNegative = false, min, max } = {}) {
  const n = toPayloadNumber(value, BLANK);
  if (n === BLANK) return null;
  if (n === null) return 'malformed';
  if (!allowNegative && n < 0) return 'negative';
  if (integer && !Number.isInteger(n)) return 'fraction';
  if (min != null && n < Number(min)) return 'too_small';
  if (max != null && n > Number(max)) return 'too_large';
  return null;
}

function NumberInput({
  value,
  onChange,
  placeholder,
  className,
  disabled,
  integer = false,
  min,
  max,
  step,
  allowNegative = false,
  testId,
  id,
  name,
  ...rest
}) {
  const {
    focused, setFocused, rawValue, focusedDisplay, emitTyped, handleKeyDown, dropHandler,
  } = useUnreadableNumberEntry({ value, onChange, marker: MALFORMED_NUMBER, disabled });
  const handleDrop = dropHandler((text) => text.trim());

  return (
    <input
      {...rest}
      id={id}
      name={name}
      type={focused ? 'number' : 'text'}
      inputMode={integer ? 'numeric' : 'decimal'}
      step={step ?? (integer ? '1' : 'any')}
      min={min ?? (allowNegative ? undefined : '0')}
      max={max}
      value={focused ? focusedDisplay : rawValue}
      onInput={(e) => emitTyped(e, true)}
      onChange={(e) => emitTyped(e, false)}
      onKeyDown={handleKeyDown}
      onDrop={handleDrop}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      data-testid={testId}
    />
  );
}

export default NumberInput;
